import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Edit } from '@mui/icons-material';
import adminService from '../../services/adminService';
import { ADMIN_ROLES } from '../../constants/adminRoles';

const initialForm = {
  name: '',
  email: '',
  role: ADMIN_ROLES.ADMIN,
  status: 'active',
  password: '',
};

const AdminRolesManagement = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAdminId, setEditingAdminId] = useState('');
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isEditing = Boolean(editingAdminId);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminService.getAdminAccounts();
      setAdmins(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching admin accounts:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Failed to fetch admin accounts',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const openCreateDialog = () => {
    setEditingAdminId('');
    setForm(initialForm);
    setDialogOpen(true);
  };

  const openEditDialog = (admin) => {
    setEditingAdminId(admin._id);
    setForm({
      name: admin.name || '',
      email: admin.email || '',
      role: admin.role || ADMIN_ROLES.ADMIN,
      status: admin.status || 'active',
      password: '',
    });
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingAdminId('');
    setForm(initialForm);
  };

  const handleFormChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const submitForm = async () => {
    try {
      const trimmedName = form.name.trim();
      const trimmedEmail = form.email.trim().toLowerCase();

      if (!trimmedName || !trimmedEmail || !form.role) {
        setSnackbar({
          open: true,
          message: 'Name, email, and role are required',
          severity: 'warning',
        });
        return;
      }

      if (!isEditing && !form.password.trim()) {
        setSnackbar({
          open: true,
          message: 'Password is required while creating admin account',
          severity: 'warning',
        });
        return;
      }

      const payload = {
        name: trimmedName,
        email: trimmedEmail,
        role: form.role,
      };

      if (isEditing) {
        payload.status = form.status;
        if (form.password.trim()) {
          payload.password = form.password.trim();
        }
        await adminService.updateAdminAccount(editingAdminId, payload);
      } else {
        payload.password = form.password.trim();
        await adminService.createAdminAccount(payload);
      }

      setSnackbar({
        open: true,
        message: isEditing ? 'Admin account updated successfully' : 'Admin account created successfully',
        severity: 'success',
      });
      closeDialog();
      fetchAdmins();
    } catch (error) {
      console.error('Error saving admin account:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.message || 'Failed to save admin account',
        severity: 'error',
      });
    }
  };

  const columns = useMemo(
    () => [
      { field: 'name', headerName: 'Name', flex: 1 },
      { field: 'email', headerName: 'Email', flex: 1.2 },
      {
        field: 'role',
        headerName: 'Role',
        width: 140,
        renderCell: (params) => (
          <Chip
            label={String(params.value || '').toUpperCase()}
            size="small"
            color={params.value === ADMIN_ROLES.STAFF ? 'default' : 'primary'}
          />
        ),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (params) => (
          <Chip
            label={params.value === 'active' ? 'Active' : 'Inactive'}
            size="small"
            color={params.value === 'active' ? 'success' : 'warning'}
          />
        ),
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Button
            size="small"
            startIcon={<Edit />}
            onClick={() => openEditDialog(params.row)}
            disabled={params.row.role === ADMIN_ROLES.SUPERADMIN}
          >
            Edit
          </Button>
        ),
      },
    ],
    []
  );

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ mb: 1 }}>
            Admin Roles
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and edit admin and staff accounts. Only superadmin can access this section.
          </Typography>
        </Box>

        <Button variant="contained" startIcon={<Add />} onClick={openCreateDialog}>
          Add Admin User
        </Button>
      </Stack>

      <Paper sx={{ height: 620, width: '100%', borderRadius: 3, p: 1 }}>
        <DataGrid
          rows={admins}
          columns={columns}
          loading={loading}
          getRowId={(row) => row._id}
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 10 },
            },
          }}
          disableRowSelectionOnClick
        />
      </Paper>

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{isEditing ? 'Edit Admin User' : 'Add Admin User'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => handleFormChange('name', e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => handleFormChange('email', e.target.value)}
              fullWidth
              required
            />
            <FormControl fullWidth>
              <InputLabel id="admin-role-label">Role</InputLabel>
              <Select
                labelId="admin-role-label"
                label="Role"
                value={form.role}
                onChange={(e) => handleFormChange('role', e.target.value)}
              >
                <MenuItem value={ADMIN_ROLES.ADMIN}>Admin</MenuItem>
                <MenuItem value={ADMIN_ROLES.STAFF}>Staff</MenuItem>
              </Select>
            </FormControl>
            {isEditing && (
              <FormControl fullWidth>
                <InputLabel id="admin-status-label">Status</InputLabel>
                <Select
                  labelId="admin-status-label"
                  label="Status"
                  value={form.status}
                  onChange={(e) => handleFormChange('status', e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            )}
            <TextField
              label={isEditing ? 'New Password (optional)' : 'Password'}
              type="password"
              value={form.password}
              onChange={(e) => handleFormChange('password', e.target.value)}
              fullWidth
              required={!isEditing}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={submitForm}>
            {isEditing ? 'Save Changes' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminRolesManagement;
